/** One named refusal of a request field (the app factory's 422 `problems` rows). */
export interface ApiErrorProblem {
  field: string;
  message: string;
}

export interface IApiError {
  httpStatusCode: number;
  message: string;
  data?: Record<string, string>;
  url?: string;
  code?: string;
  problems?: ApiErrorProblem[];
  availableFrom?: number;
  availableTo?: number;
  /** `httpStatusCode` from the platform VError body; may differ from the wire status. */
  bodyStatusCode?: number;
}

export class ApiError extends Error {
  readonly httpStatusCode: number;
  readonly data?: Record<string, string>;
  readonly url?: string;
  /**
   * Structured error code: the analytics `{error:{code,...}}` envelope (ADR-003 §8)
   * or the app factory's top-level `{code, message}` (relayed verbatim by the api).
   */
  readonly code?: string;
  /** The app factory's `{code:"validation_failed", problems:[{field, message}]}` rows, when present. */
  readonly problems?: ApiErrorProblem[];
  /** UTC epoch seconds; present on coverage/product-window errors. */
  readonly availableFrom?: number;
  /** UTC epoch seconds; present on coverage errors. */
  readonly availableTo?: number;
  /** `httpStatusCode` from the platform VError body; may differ from the wire status. */
  readonly bodyStatusCode?: number;

  constructor(error: IApiError) {
    super(error.message);
    this.name = 'ApiError';
    this.httpStatusCode = error.httpStatusCode;
    this.data = error.data;
    this.url = error.url;
    this.code = error.code;
    this.problems = error.problems;
    this.availableFrom = error.availableFrom;
    this.availableTo = error.availableTo;
    this.bodyStatusCode = error.bodyStatusCode;
  }

  get isConflict(): boolean {
    return this.httpStatusCode === 409;
  }

  get isAuthError(): boolean {
    return this.httpStatusCode === 401 || this.httpStatusCode === 403;
  }

  /**
   * Platform authentication rejection: the backend's rejection handler completes
   * expired/invalid sessions as wire 403 with a VError body of httpStatusCode 401
   * ("Not authenticated.") — never wire 401 — so the wire status alone cannot
   * identify them (backend customRejectionHandler; gateway contract §Error contract).
   * Wrong-credential logins share this signature, so WebApi treats it as session
   * expiry only on requests that were sent with an auth token.
   */
  get isNotAuthenticated(): boolean {
    return this.httpStatusCode === 403 && this.bodyStatusCode === 401;
  }

  get isNotFound(): boolean {
    return this.httpStatusCode === 404;
  }

  /**
   * Guard for single-entity GET responses. Backend routes complete Scala
   * `Option` results directly, so "not found / not owned by this cId" arrives
   * as 200 with an empty body, which WebApi parses to `{}` — truthy, and it
   * slips past `!data` guards (crashes on first nested read, e.g. after a
   * sub-account switch re-fires a detail query with the new cId). Probing one
   * required top-level field converts that into a proper 404 ApiError at the
   * API boundary, so the declared response type actually holds for callers.
   */
  static requireFound<T extends object>(value: T | null | undefined, probeKey: keyof T, label: string): T {
    if (value == null || value[probeKey] === undefined) {
      throw new ApiError({ httpStatusCode: 404, message: `${label} not found` });
    }
    return value;
  }

  /**
   * Detect the platform VError envelope on a WIRE-2xx body. The `/v1`
   * catch-all completes uncaught server Throwables as HTTP 200 with
   * `{"httpStatusCode":500,"msg":"Server error.","data":{}}` — parsed as the
   * happy-path type, a server failure masquerades as success (mutations toast
   * green, uploads think they registered). No legitimate platform payload
   * carries a top-level numeric `httpStatusCode` + string `msg`/`safeMsg`.
   * Returns the ApiError to throw, or null when the body is a real payload.
   */
  static fromErrorEnvelope(body: unknown, wireStatus: number, url: string): ApiError | null {
    if (typeof body !== 'object' || body === null || Array.isArray(body)) return null;
    const b = body as Record<string, unknown>;
    const bodyStatusCode = b.httpStatusCode;
    if (typeof bodyStatusCode !== 'number' || bodyStatusCode < 400) return null;
    const msg = typeof b.safeMsg === 'string' ? b.safeMsg : typeof b.msg === 'string' ? b.msg : null;
    if (msg === null) return null;
    return new ApiError({
      httpStatusCode: wireStatus,
      message: msg,
      data: (typeof b.data === 'object' && b.data !== null ? b.data : undefined) as
        | Record<string, string>
        | undefined,
      url,
      bodyStatusCode,
    });
  }
}
